"use client";

import {
	type CSSProperties,
	type FormEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	DSFont,
	DSRadius,
	DSSpacing,
} from "@/components/design-system/ArtistDesignSystem";
import {
	GenArtArtworkCard,
	GenArtArtworkPlaceholderCard,
} from "./GenArtArtworkCard";
import { GenArtMetric } from "./GenArtMetric";
import {
	type DesignerPromptSuggestion,
	formatGenerationPrice,
	type GenArtModelDescriptor,
	generateMusicButtonTitle,
} from "./genArtCatalog";
import { useTranslation } from "@/i18n/ArtistI18n";

const PROMPT_SUGGESTION_SKELETON_IDS: readonly string[] = Array.from(
	{ length: 3 },
	(_, index) => `prompt-suggestion-skeleton-${index}`,
);

export interface GenArtBottomInfoCardProps {
	readonly selectedModel: GenArtModelDescriptor | null;
	readonly prompt: string;
	readonly onPromptChange: (value: string) => void;
	readonly isPromptFocused: boolean;
	readonly onPromptFocusChange: (focused: boolean) => void;
	readonly promptSuggestions: readonly DesignerPromptSuggestion[];
	readonly isPromptSuggestionsLoading: boolean;
	readonly isGenerating: boolean;
	readonly onGenerate: () => void;
	readonly tutorialMode?: boolean;
	readonly tutorialSimulatedCredits?: number;
	readonly onPromptSuggestionSelect?: (suggestion: DesignerPromptSuggestion) => void;
}

const CONFIRMATION_TIMEOUT_MS = 3000;

const cardStyle: CSSProperties = {
	position: "relative",
	display: "flex",
	flexDirection: "column",
	gap: 0,
	background: "var(--ds-surface)",
	borderTopLeftRadius: DSRadius.xl,
	borderTopRightRadius: DSRadius.xl,
	borderBottomLeftRadius: 0,
	borderBottomRightRadius: 0,
	color: "var(--ds-text-primary)",
};

const titleStyle: CSSProperties = {
	font: DSFont.genArtHeavy(26),
	lineHeight: 1.1,
	margin: 0,
	padding: `0 24px`,
};

const metricsRowStyle: CSSProperties = {
	display: "grid",
	gridTemplateColumns: "1fr 1fr 1fr",
	alignItems: "center",
	padding: `0 24px`,
};

const suggestionsScrollerStyle: CSSProperties = {
	display: "flex",
	gap: "12px",
	overflowX: "auto",
	scrollSnapType: "x mandatory",
	scrollbarWidth: "none",
	padding: "0 24px",
	WebkitOverflowScrolling: "touch",
};

const promptWrapperStyle: CSSProperties = {
	position: "relative",
	margin: `0 24px`,
	borderRadius: "14px",
	background: "color-mix(in srgb, var(--ds-background) 70%, transparent)",
};

const placeholderStyle: CSSProperties = {
	position: "absolute",
	top: "10px",
	left: "12px",
	right: "12px",
	font: DSFont.genArtRegular(16),
	color: "color-mix(in srgb, var(--ds-text-secondary) 78%, transparent)",
	pointerEvents: "none",
};

const textareaStyle: CSSProperties = {
	width: "100%",
	minHeight: "40px",
	maxHeight: "96px",
	border: 0,
	outline: 0,
	padding: "10px 12px",
	background: "transparent",
	color: "var(--ds-text-primary)",
	font: DSFont.genArtRegular(16),
	lineHeight: 1.4,
	resize: "none",
	borderRadius: "14px",
};

const generateButtonStyle = (
	tint: string,
	foreground: string,
	enabled: boolean,
): CSSProperties => ({
	width: "100%",
	minHeight: "50px",
	padding: "14px 24px",
	borderRadius: DSRadius.full,
	border: 0,
	font: DSFont.genArtHeavy(18),
	color: foreground,
	background: enabled
		? tint
		: "color-mix(in srgb, var(--ds-text-secondary) 45%, transparent)",
	cursor: enabled ? "pointer" : "not-allowed",
	boxShadow: enabled
		? `0 12px 32px color-mix(in srgb, ${tint} 32%, transparent)`
		: "none",
	transition: "background 220ms ease, color 220ms ease, box-shadow 220ms ease",
});

/**
 * GenArtBottomInfoCard — the medium-detent surface pinned to the bottom of
 * the GenArt scene. Mirrors iOS `GenArtBottomInfoCard`:
 *
 * - Title "GenArt {model}" (genArtHeavy 26px)
 * - 3-column model parameter row (genArtHeavy 20px title, genArtRegular 12px
 *   secondary subtitle) — hidden when the prompt is focused so the sheet
 *   collapses gracefully on small viewports
 * - Horizontal scroll-snap strip of 216×118 suggestion cards (with skeleton
 *   placeholders while the designer content is still loading)
 * - Auto-growing 1–3 line prompt textarea (genArtRegular 16px,
 *   background 0.7, 14px radius, secondary 0.78 placeholder)
 * - Glass-prominent generate button (genArtHeavy 18px, 14px vertical padding)
 *   with the iOS double-tap price confirmation flow (3s reset)
 */
export function GenArtBottomInfoCard({
	selectedModel,
	prompt,
	onPromptChange,
	isPromptFocused,
	onPromptFocusChange,
	promptSuggestions,
	isPromptSuggestionsLoading,
	isGenerating,
	onGenerate,
	tutorialMode = false,
	tutorialSimulatedCredits,
	onPromptSuggestionSelect,
}: Readonly<GenArtBottomInfoCardProps>) {
	const { t } = useTranslation();
	const [isConfirmationArmed, setIsConfirmationArmed] = useState(false);
	const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	const hasPrompt = prompt.trim().length > 0;
	const isGenerateEnabled =
		hasPrompt && !isGenerating && (tutorialMode || selectedModel !== null);
	const generationPrice = selectedModel?.musicGenerationPrice ?? null;
	const requiresConfirmation =
		!tutorialMode && isGenerateEnabled && generationPrice !== null && generationPrice > 0;
	const isConfirming = requiresConfirmation && isConfirmationArmed;

	const tint = isConfirming
		? "var(--ds-accent)"
		: hasPrompt
			? (selectedModel?.callToActionTint ?? "var(--ds-accent)")
			: "color-mix(in srgb, var(--ds-text-secondary) 45%, transparent)";
	const foreground = isConfirming
		? "var(--ds-text-primary)"
		: "rgb(255 255 255)";

	const buttonTitle = isGenerating
		? t("genart.generating")
		: isConfirming
			? t("genart.confirmGenerate", { price: formatGenerationPrice(generationPrice ?? 0) })
			: generateMusicButtonTitle(t("genart.generateMusic"), generationPrice);

	const resetConfirmation = useCallback(() => {
		if (confirmationTimerRef.current !== null) {
			clearTimeout(confirmationTimerRef.current);
			confirmationTimerRef.current = null;
		}
		setIsConfirmationArmed(false);
	}, []);

	useEffect(() => {
		return () => {
			if (confirmationTimerRef.current !== null) {
				clearTimeout(confirmationTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		resetConfirmation();
	}, [resetConfirmation]);

	const autoGrow = useCallback(() => {
		const el = textareaRef.current;
		if (el === null) return;
		el.style.height = "auto";
		const next = Math.min(el.scrollHeight, 96);
		el.style.height = `${Math.max(next, 40)}px`;
	}, []);

	useEffect(() => {
		autoGrow();
	}, [autoGrow]);

	const handleGenerateTap = () => {
		onPromptFocusChange(false);
		if (!requiresConfirmation) {
			onGenerate();
			return;
		}
		if (isConfirmationArmed) {
			resetConfirmation();
			onGenerate();
			return;
		}
		setIsConfirmationArmed(true);
		if (confirmationTimerRef.current !== null) {
			clearTimeout(confirmationTimerRef.current);
		}
		confirmationTimerRef.current = setTimeout(() => {
			setIsConfirmationArmed(false);
			confirmationTimerRef.current = null;
		}, CONFIRMATION_TIMEOUT_MS);
	};

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		handleGenerateTap();
	};

	const handleSuggestionSelect = (suggestion: DesignerPromptSuggestion) => {
		onPromptChange(suggestion.prompt);
		onPromptSuggestionSelect?.(suggestion);
	};

	const uiSpec = selectedModel?.uiSpec;

	return (
		<form
			className="genart-bottom-card"
			style={cardStyle}
			onSubmit={handleSubmit}
		>
			<h2 style={titleStyle}>GenArt {selectedModel?.displayName ?? ""}</h2>

			{!isPromptFocused && uiSpec ? (
				<div
					style={{
						...metricsRowStyle,
						paddingTop: DSSpacing.md,
						paddingBottom: DSSpacing.md,
					}}
				>
					<GenArtMetric spec={uiSpec.param1} align="left" />
					<GenArtMetric spec={uiSpec.param2} align="center" />
					<GenArtMetric spec={uiSpec.param3} align="right" />
				</div>
			) : null}

			{tutorialMode && tutorialSimulatedCredits !== undefined ? (
				<div style={{ padding: "0 24px", color: "var(--ds-accent)", fontWeight: 700, fontSize: "13px" }}>
					{t("genart.simulatedInspo", { credits: tutorialSimulatedCredits.toLocaleString() })}
				</div>
			) : null}

			<div
				style={{
					overflow: "hidden",
					paddingTop: DSSpacing.md,
					paddingBottom: DSSpacing.md,
				}}
			>
				<div style={suggestionsScrollerStyle}>
					{promptSuggestions.length === 0 ? (
						isPromptSuggestionsLoading ? (
							PROMPT_SUGGESTION_SKELETON_IDS.map((skeletonId) => (
								<GenArtArtworkPlaceholderCard
									key={skeletonId}
									caption={t("genart.loading")}
									isLoading
								/>
							))
						) : (
							<GenArtArtworkPlaceholderCard
								caption={t("genart.loadFailed")}
								isLoading={false}
							/>
						)
					) : (
						promptSuggestions.map((suggestion) => (
							<GenArtArtworkCard
								key={suggestion.id}
								suggestion={suggestion}
								onSelect={handleSuggestionSelect}
							/>
						))
					)}
				</div>
			</div>

			<div style={{ ...promptWrapperStyle, marginTop: DSSpacing.md }}>
				{prompt.length === 0 ? (
					<span style={placeholderStyle}>{t("genart.promptPlaceholder")}</span>
				) : null}
				<textarea
					ref={textareaRef}
					aria-label={t("genart.prompt")}
					rows={1}
					value={prompt}
					onChange={(event) => onPromptChange(event.currentTarget.value)}
					onFocus={() => onPromptFocusChange(true)}
					onBlur={() => onPromptFocusChange(false)}
					style={textareaStyle}
				/>
			</div>

			<button
				type="submit"
				disabled={!isGenerateEnabled}
				style={{
					...generateButtonStyle(tint, foreground, isGenerateEnabled),
					marginTop: DSSpacing.md,
					marginLeft: "24px",
					marginRight: "24px",
					marginBottom: "max(24px, env(safe-area-inset-bottom, 0px))",
				}}
			>
				{buttonTitle}
			</button>
		</form>
	);
}
